import cookieUtils from 'js-cookie';
import { GLOBAL } from 'saddlebag-browser';
import * as cookieParser from 'skyscanner-cookie-parser';

import { storeCookie } from './storeCookie';

import type { CookieOptions, MultiValueCookieInterface } from './types';

export default class MultiValueCookie {
  name: string;

  cookie: MultiValueCookieInterface;

  /**
   * Optional injectable store function for testing purposes.
   * When set, this function will be called instead of the default storeCookie.
   * @internal Used by saddlebag-tracking-preferences for test mocking
   */
  storeInternal?: (
    name: string,
    value: string,
    expires: number | null | undefined,
    options?: CookieOptions,
  ) => void;

  /**
   * @param name Cookie name
   *
   * @example
   * let scannerCookie = new Cookie('scanner')
   */
  constructor(name: string) {
    this.name = name;
    const doc = GLOBAL.getDocument() as Document;
    const cookies = doc.cookie;
    const parsedCookie = cookieParser.parseMultiValueCookie(name, cookies);
    if (parsedCookie) {
      this.cookie = parsedCookie;
    } else {
      this.cookie = new cookieParser.MultiValueCookie();
    }
  }

  /**
   * Gets a single value from the multivalue cookie
   * @param key Key of the value requested
   * @returns Value of the selected key
   */
  getValue(key: string): string | null {
    return this.cookie.getValue(key);
  }

  /**
   * Sets or edits a single value in the multivalue cookie
   * @param key Key of the value to be set
   * @param value New value to be set
   * @returns this
   */
  setValue(key: string, value: unknown): this {
    this.cookie.setValue(key, value);
    return this;
  }

  /**
   * Deletes a single value from the multivalue cookie. Note that this deletes the key as well.
   * @param key Key of the value selected for deletion
   * @returns this
   */
  deleteValue(key: string): this {
    this.cookie.deleteValue(key);
    return this;
  }

  /**
   * Returns an array of all keys used in the cookie
   * @returns array of keys
   */
  keys(): string[] {
    return this.cookie.keys();
  }

  /**
   * Clears all the multivalue cookie values
   * @returns this
   */
  clear(): this {
    this.cookie.clear();
    return this;
  }

  /**
   * Returns entire cookie value containing all key/value pairs as a string
   * @returns String containing all key/value pairs
   */
  getRawValue(): string {
    return this.cookie.serializeToString();
  }

  /**
   * Stores full cookie in the document
   * @param expires Number of days for cookie to be valid for
   * @param options object for optional parameters
   * @returns this
   */
  store(expires: number | null | undefined, options?: CookieOptions): this {
    const storeFunc = this.storeInternal ?? storeCookie;
    storeFunc(this.name, this.cookie.serializeToString(), expires, options);
    return this;
  }

  /**
   * Removes the cookie
   */
  remove(): void {
    cookieUtils.remove(this.name);
  }
}
